import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Box,
  Rating,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  Slider,
  Button,
  Snackbar
} from '@mui/material';
import {
  ShoppingCart,
  Search,
  FilterList,
  Clear,
  Refresh,
  CheckCircle
} from '@mui/icons-material';
import { products, categories } from '../data/dummyData';
import MainService from '../services/MainService';
import config from '../services/config.json';

const Products = ({ addToCart }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // URL Parameters
  const searchParams = new URLSearchParams(location.search);
  const categoryIdFromUrl = searchParams.get('categoryId');

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryIdFromUrl || 'all');
  const [sortBy, setSortBy] = useState('name');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [currentPage, setCurrentPage] = useState(1);

  // API Data States
  const [apiProducts, setApiProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cart notification state
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [addedProductName, setAddedProductName] = useState('');

  // Pagination
  const itemsPerPage = 20;
  const skip = (currentPage - 1) * itemsPerPage;

  // API Call Functions
  const fetchCategories = async () => {
    try {
      const response = await MainService.CategoriesInDropdown();
      setApiCategories(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const filterData = {
        MinPrice: minPrice,
        MaxPrice: maxPrice,
        CategoryIds: selectedCategory === 'all' ? [] : [parseInt(selectedCategory)],
        SortType: getSortType(sortBy),
        Skip: skip,
        Take: itemsPerPage,
        Query: searchTerm,
        SubCategoryIds: [],
        BrandIds: [],
        SizeIds: [],
        ColorIds: [],
      };

      const response = await MainService.ProductsByFilter(filterData);
      setApiProducts(response.data || response || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const getSortType = (sortBy) => {
    switch (sortBy) {
      case 'price-low':
        return 'PLH';
      case 'price-high':
        return 'PHL';
      case 'top-selling':
        return 'TS';
      default:
        return 'PLH';
    }
  };

  // Call APIs on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch products when filters change (except search term)
  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchProducts();
  }, [selectedCategory, sortBy, minPrice, maxPrice]);

  // Set category from URL parameter
  useEffect(() => {
    if (categoryIdFromUrl) {
      setSelectedCategory(categoryIdFromUrl);
    }
  }, [categoryIdFromUrl]);

  // Handle pagination changes
  useEffect(() => {
    fetchProducts();
  }, [currentPage]);

  const handleAddToCart = (product) => {
    addToCart(product);
    setAddedProductName(product.Name || product.name || 'Item');
    setShowCartNotification(true);
  };

  const handleViewProduct = (productId, productName) => {
    navigate(`/product/${productId}?name=${encodeURIComponent(productName)}`);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortBy('name');
    setMinPrice(0);
    setMaxPrice(10000);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchProducts();
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortBy('name');
    setMinPrice(0);
    setMaxPrice(10000);
    setCurrentPage(1);
    // Call API after clearing filters
    setTimeout(() => {
      fetchProducts();
    }, 100);
  };

  // Use API data if available, otherwise fallback to dummy data
  const displayProducts = apiProducts.length > 0 ? apiProducts : products;
  const displayCategories = apiCategories.length > 0 ? apiCategories : categories;

  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
        All Products
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        Discover our fresh and quality products
      </Typography>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 4, p: 3, backgroundColor: '#f5f5f5', borderRadius: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{
                  backgroundColor: '#2E7D32',
                  color: 'white',
                  minWidth: 'auto',
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#1B5E20',
                  },
                }}
              >
                Search
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {displayCategories.map((category) => (
                  <MenuItem key={category.Id || category.id} value={category.Id || category.id}>
                    {category.Name || category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
                sx={{ minWidth: '200px' }}
              >
                <MenuItem value="price-low">Price: Low to High</MenuItem>
                <MenuItem value="price-high">Price: High to Low</MenuItem>
                <MenuItem value="top-selling">Top Selling</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ px: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Price Range: ₹{minPrice} - ₹{maxPrice}
              </Typography>
              <Slider
                value={[minPrice, maxPrice]}
                onChange={(event, newValue) => {
                  setMinPrice(newValue[0]);
                  setMaxPrice(newValue[1]);
                }}
                valueLabelDisplay="auto"
                min={0}
                max={10000}
                step={10}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<FilterList />}
                label={`${displayProducts.length} products`}
                color="primary"
                sx={{ backgroundColor: '#2E7D32' }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                startIcon={<Clear />}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                startIcon={<Refresh />}
                sx={{
                  borderColor: '#2E7D32',
                  color: '#2E7D32',
                  '&:hover': {
                    borderColor: '#1B5E20',
                    backgroundColor: 'rgba(46, 125, 50, 0.04)',
                  },
                }}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <CircularProgress sx={{ color: '#2E7D32' }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Products Grid */}
      {displayProducts.length > 0 ? (
        <Grid container spacing={3}>
          {displayProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id || product.Id || Math.random()}>
              <Card
                sx={{
                  //height: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={config.API_BASE_URL + (product.Image1 || product.image1 || product.image || '')}
                  alt={product.Name || product.name || 'Product'}
                  sx={{ cursor: 'pointer' }}
                                     onClick={() => handleViewProduct(product.Id || product.id, product.Name || product.name)}
                />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {product.Name || product.name || 'Product Name'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {product.Size?.Name || 'Size'}  - {product.Category?.Name || product.category?.name || 'Category'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 2 }}>
                      {product.OfferPrice && product.Price && product.OfferPrice !== product.Price && (
                        <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          ₹{product.Price || product.price || '0.00'}
                        </Typography>
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                        ₹{product.OfferPrice || product.offerPrice || product.Price || product.price || '0.00'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary">
            No products found matching your criteria
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your search or filter options
          </Typography>
        </Box>
      )}

      {/* Pagination */}
      {displayProducts.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="outlined"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
            sx={{ mr: 2 }}
          >
            Previous
          </Button>
          <Typography variant="body1" sx={{ alignSelf: 'center', mx: 2 }}>
            Page {currentPage}
          </Typography>
          <Button
            variant="outlined"
            disabled={displayProducts.length < itemsPerPage}
            onClick={() => setCurrentPage(prev => prev + 1)}
            sx={{ ml: 2 }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Cart Notification Snackbar */}
      <Snackbar
        open={showCartNotification}
        autoHideDuration={3000}
        onClose={() => setShowCartNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowCartNotification(false)}
          severity="success"
          icon={<CheckCircle />}
          sx={{ width: '100%' }}
        >
          {addedProductName} added to cart successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Products; 